import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Bot, Cpu, Activity } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, CardHeader } from "@/components/common";
import { relativeTime } from "@/lib/format";

export default function Agents() {
  const { companyParam, companyId } = useApp();
  const { data: agents = [] } = useQuery({
    queryKey: ["agents", companyId],
    queryFn: async () => (await api.get("/agents", { params: companyParam })).data,
  });
  const { data: runs = [] } = useQuery({
    queryKey: ["agent-runs", companyId],
    queryFn: async () => (await api.get("/agent-runs", { params: { ...companyParam, limit: 40 } })).data,
  });

  const shared = agents.filter((a) => a.scope === "shared");
  const domain = agents.filter((a) => a.scope !== "shared");

  return (
    <div data-testid="agents-page">
      <PageHeader title="Agents" subtitle="A shared orchestration core plus custom-domained agents. Each agent only sees the tools, templates and rules relevant to its domain." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader title="Shared Core Agents" icon={Cpu} />
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-border">
              {shared.map((a) => <AgentCell key={a.id} a={a} />)}
            </div>
          </Card>
          <Card>
            <CardHeader title="Company-Specific Agents" icon={Bot} />
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-border">
              {domain.map((a) => <AgentCell key={a.id} a={a} />)}
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader title="Execution Stream" icon={Activity} action={<span className="w-2 h-2 bg-st-success live-dot" />} />
          <div className="divide-y divide-border max-h-[560px] overflow-y-auto">
            {runs.map((r) => (
              <div key={r.id} className="px-4 py-2.5" data-testid={`agent-run-${r.id}`}>
                <div className="flex items-center gap-2">
                  <Bot className="w-3.5 h-3.5 text-brand shrink-0" />
                  <span className="text-xs font-semibold text-ink truncate flex-1">{r.agent_name}</span>
                  <span className="font-mono text-[10px] text-ink-faint">{relativeTime(r.started_at)}</span>
                </div>
                <p className="text-xs text-ink-muted mt-0.5 pl-5">{r.summary}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AgentCell({ a }) {
  return (
    <div className="p-4 border-border" data-testid={`agent-${a.key}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-ink text-white flex items-center justify-center"><Bot className="w-4 h-4" /></div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink leading-tight truncate">{a.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink-faint">{a.domain}</div>
        </div>
      </div>
      <p className="text-xs text-ink-muted mt-2 leading-snug">{a.desc}</p>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
        <span className="text-[10px] uppercase tracking-wider text-st-success font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-st-success" /> Active</span>
        <span className="font-mono text-[11px] text-ink-faint">{a.runs} runs</span>
      </div>
    </div>
  );
}
